import { useState } from "react";
import QuestionLayout from "./QuestionLayout";
import { renderStaffLines, renderClef } from "./staff/staffRender";
import { snapYToPitch, pitchToY } from "./staff/pitchMath";
import { Glyph } from "./staff/glyphs.jsx";

const STAFF_HEIGHT = 60;
const STAFF_GAP = 50;

const TREBLE_STAFF_TOP = 40;
const BASS_STAFF_TOP =
  TREBLE_STAFF_TOP + STAFF_HEIGHT + STAFF_GAP;

const LEFT_MARGIN = 100;
const DEFAULT_SLOT_WIDTH = 140;

const SYSTEM_TOP = TREBLE_STAFF_TOP;
const SYSTEM_BOTTOM =
  BASS_STAFF_TOP + STAFF_HEIGHT;

const MAX_ACCIDENTALS = 7;
const ACCIDENTAL_SPACING = 12;
const ACCIDENTAL_START_X = 20;

const BRACE_PATH =
  "M-12 -656c0 -352 -132 -696 -132 -1032c0 -180 36 -344 156 -484c4 -4 8 -8 8 -12c0 -8 -12 -16 -20 -16c-4 0 -8 0 -12 4c-160 184 -216 416 -216 656c0 360 140 712 140 1052c0 176 -40 344 -160 480v8v8c120 136 160 304 160 480c0 340 -140 692 -140 1052 c0 240 56 472 216 656c4 4 8 4 12 4c8 0 20 -8 20 -16c0 -4 -4 -8 -8 -12c-120 -140 -156 -304 -156 -484c0 -336 132 -680 132 -1032c0 -240 -52 -472 -208 -656c156 -184 208 -416 208 -656z";

export default function GrandStaffKeySignatureInputWidget({
  config,
  value = {},
  onChange,
  mode = "edit",
  feedback
}) {
  const {
    number,
    prompt,
    points,
    slots = 1,
    slotWidth = DEFAULT_SLOT_WIDTH,
    allowLedgerLines = false
  } = config;

  const [activeAccidental, setActiveAccidental] =
    useState(null);

  // Used only for mouse/trackpad hover preview.
  const [hover, setHover] = useState(null);

  const accidentals = value.accidentals || {
    treble: {},
    bass: {}
  };

  const staffWidth =
    LEFT_MARGIN + slots * slotWidth;

  const svgHeight =
    BASS_STAFF_TOP + STAFF_HEIGHT + 70;

  function staffTop(staff) {
    return staff === "treble"
      ? TREBLE_STAFF_TOP
      : BASS_STAFF_TOP;
  }

  function clefForStaff(staff) {
    return staff;
  }

  function staffFromY(y) {
    const trebleLow =
      TREBLE_STAFF_TOP - 40;

    const trebleHigh =
      TREBLE_STAFF_TOP +
      STAFF_HEIGHT +
      STAFF_GAP / 2;

    const bassLow =
      BASS_STAFF_TOP - 40;

    const bassHigh =
      BASS_STAFF_TOP +
      STAFF_HEIGHT +
      40;

    if (
      y >= trebleLow &&
      y <= trebleHigh
    ) {
      return "treble";
    }

    if (
      y >= bassLow &&
      y <= bassHigh
    ) {
      return "bass";
    }

    return null;
  }

  function getPointerLocation(event, expectedStaff) {
    const svg =
      event.currentTarget.ownerSVGElement;

    if (!svg) return null;

    const screenCTM = svg.getScreenCTM();
    if (!screenCTM) return null;

    const point = svg.createSVGPoint();

    point.x = event.clientX;
    point.y = event.clientY;

    const svgPoint = point.matrixTransform(
      screenCTM.inverse()
    );

    const resolvedStaff =
      staffFromY(svgPoint.y);

    if (resolvedStaff !== expectedStaff) {
      return null;
    }

    const pitch = snapYToPitch(
      svgPoint.y,
      clefForStaff(expectedStaff),
      {
        staffTop: staffTop(expectedStaff),
        allowLedgerLines
      }
    );

    return {
      x: svgPoint.x,
      y: svgPoint.y,
      pitch
    };
  }

  function placeAccidental(
    slot,
    staff,
    pitch
  ) {
    if (!activeAccidental) return;
    if (!pitch) return;

    const existing =
      accidentals?.[staff]?.[slot] || [];

    if (
      existing.length >= MAX_ACCIDENTALS
    ) {
      return;
    }

    onChange({
      ...value,
      accidentals: {
        treble:
          accidentals.treble || {},
        bass:
          accidentals.bass || {},
        [staff]: {
          ...(accidentals[staff] || {}),
          [slot]: [
            ...existing,
            {
              pitch,
              accidental:
                activeAccidental
            }
          ]
        }
      }
    });
  }

  function removeAccidental(
    slot,
    staff,
    index
  ) {
    const existing =
      accidentals?.[staff]?.[slot] || [];

    const next = existing.filter(
      (_, i) => i !== index
    );

    onChange({
      ...value,
      accidentals: {
        treble:
          accidentals.treble || {},
        bass:
          accidentals.bass || {},
        [staff]: {
          ...(accidentals[staff] || {}),
          [slot]: next
        }
      }
    });
  }

  function slotFeedback(slot, staff) {
    return feedback?.parts?.[slot]?.[staff];
  }

  return (
    <QuestionLayout
      number={number}
      prompt={prompt}
      points={points}
      mode={mode}
      feedback={feedback}
    >
      <div>
        {mode === "edit" && (
          <div style={{ marginBottom: 10 }}>
            <button
              type="button"
              onClick={() => {
                setActiveAccidental("b");
                setHover(null);
              }}
              style={{
                marginRight: 6,
                border:
                  activeAccidental === "b"
                    ? "2px solid #1976d2"
                    : "1px solid #aaa"
              }}
            >
              ♭ Flat
            </button>

            <button
              type="button"
              onClick={() => {
                setActiveAccidental("#");
                setHover(null);
              }}
              style={{
                border:
                  activeAccidental === "#"
                    ? "2px solid #1976d2"
                    : "1px solid #aaa"
              }}
            >
              ♯ Sharp
            </button>
          </div>
        )}

        <svg
          width={staffWidth}
          height={svgHeight}
          style={{
            touchAction:
              mode === "edit"
                ? "none"
                : "auto",
            userSelect: "none",
            WebkitUserSelect: "none"
          }}
        >
          {renderStaffLines(
            staffWidth,
            TREBLE_STAFF_TOP
          )}

          {renderStaffLines(
            staffWidth,
            BASS_STAFF_TOP
          )}

          {renderClef(
            "treble",
            TREBLE_STAFF_TOP
          )}

          {renderClef(
            "bass",
            BASS_STAFF_TOP
          )}

          <line
            x1={50}
            x2={50}
            y1={SYSTEM_TOP}
            y2={SYSTEM_BOTTOM - 20}
            stroke="#000"
            strokeWidth={1.6}
          />

          <g
            transform={`
              matrix(
                0.034, 0, 0, 0.034,
                ${LEFT_MARGIN - 55},
                ${TREBLE_STAFF_TOP + 75}
              )
            `}
          >
            <path
              d={BRACE_PATH}
              fill="#000"
            />
          </g>

          {Array.from({
            length: slots
          }).map((_, i) => {
            const slot = i + 1;

            const x =
              LEFT_MARGIN +
              i * slotWidth -
              20;

            return (
              <g key={slot}>
                {["treble", "bass"].map(
                  staff => {
                    const placed =
                      accidentals?.[staff]?.[
                        slot
                      ] || [];

                    const fb =
                      slotFeedback(
                        slot,
                        staff
                      );

                    return (
                      <g
                        key={`${slot}-${staff}`}
                      >
                        {/* HIT AREA */}
                        <rect
                          x={x}
                          y={
                            staffTop(staff) -
                            35
                          }
                          width={slotWidth}
                          height={
                            STAFF_HEIGHT + 70
                          }
                          fill="transparent"
                          stroke={
                            mode ===
                              "review" &&
                            fb
                              ? fb.correct
                                ? "#2e7d32"
                                : "#c62828"
                              : "none"
                          }
                          strokeWidth={
                            mode ===
                              "review" &&
                            fb
                              ? 2
                              : 0
                          }
                          style={{
                            cursor:
                              mode === "edit" &&
                              activeAccidental
                                ? "crosshair"
                                : "default"
                          }}
                          onPointerMove={event => {
                            if (
                              mode !== "edit"
                            ) {
                              return;
                            }

                            if (
                              !activeAccidental
                            ) {
                              return;
                            }

                            /*
                             * Touch does not have
                             * a meaningful persistent
                             * hover state.
                             */
                            if (
                              event.pointerType ===
                              "touch"
                            ) {
                              return;
                            }

                            const location =
                              getPointerLocation(
                                event,
                                staff
                              );

                            if (!location) {
                              setHover(null);
                              return;
                            }

                            setHover({
                              slot,
                              staff,
                              pitch:
                                location.pitch
                            });
                          }}
                          onPointerLeave={() => {
                            setHover(null);
                          }}
                          onPointerDown={event => {
                            if (
                              mode !== "edit"
                            ) {
                              return;
                            }

                            if (
                              !activeAccidental
                            ) {
                              return;
                            }

                            if (
                              event.pointerType ===
                                "mouse" &&
                              event.button !== 0
                            ) {
                              return;
                            }

                            event.preventDefault();
                            event.stopPropagation();

                            /*
                             * Always calculate the pitch
                             * from this exact tap/click.
                             * Never use hover.pitch.
                             */
                            const location =
                              getPointerLocation(
                                event,
                                staff
                              );

                            if (!location) return;

                            placeAccidental(
                              slot,
                              staff,
                              location.pitch
                            );

                            /*
                             * Prevent a touch-created
                             * location from remaining
                             * queued for the next tap.
                             */
                            setHover(null);
                          }}
                        />

                        {/* PLACED ACCIDENTALS */}
                        {placed.map(
                          (accidental, index) => (
                            <g
                              key={`${staff}-${slot}-${accidental.pitch}-${accidental.accidental}-${index}`}
                              transform={`translate(
                                ${
                                  x +
                                  ACCIDENTAL_START_X +
                                  index *
                                    ACCIDENTAL_SPACING
                                },
                                ${pitchToY(
                                  accidental.pitch,
                                  clefForStaff(
                                    staff
                                  ),
                                  {
                                    staffTop:
                                      staffTop(
                                        staff
                                      )
                                  }
                                )}
                              )`}
                              onPointerDown={event => {
                                if (
                                  mode !== "edit"
                                ) {
                                  return;
                                }

                                if (
                                  event.pointerType ===
                                    "mouse" &&
                                  event.button !==
                                    0
                                ) {
                                  return;
                                }

                                event.preventDefault();
                                event.stopPropagation();

                                removeAccidental(
                                  slot,
                                  staff,
                                  index
                                );

                                setHover(null);
                              }}
                              style={{
                                cursor:
                                  mode === "edit"
                                    ? "pointer"
                                    : "default"
                              }}
                            >
                              {/* HIT BOX */}
                              <rect
                                x={-12}
                                y={-16}
                                width={16}
                                height={32}
                                fill="transparent"
                                pointerEvents="all"
                              />

                              <Glyph
                                symbol="quarter_note"
                                accidental={
                                  accidental.accidental
                                }
                                accidentalOnly
                              />
                            </g>
                          )
                        )}

                        {/* DESKTOP HOVER GHOST */}
                        {hover?.slot ===
                          slot &&
                          hover?.staff ===
                            staff &&
                          activeAccidental &&
                          placed.length <
                            MAX_ACCIDENTALS && (
                            <g
                              transform={`translate(
                                ${
                                  x +
                                  ACCIDENTAL_START_X +
                                  placed.length *
                                    ACCIDENTAL_SPACING
                                },
                                ${pitchToY(
                                  hover.pitch,
                                  clefForStaff(
                                    staff
                                  ),
                                  {
                                    staffTop:
                                      staffTop(
                                        staff
                                      )
                                  }
                                )}
                              )`}
                              opacity={0.4}
                              pointerEvents="none"
                            >
                              <Glyph
                                symbol="quarter_note"
                                accidental={
                                  activeAccidental
                                }
                                accidentalOnly
                                fill="#999"
                              />
                            </g>
                          )}
                      </g>
                    );
                  }
                )}

                {config.keyPrompts?.[
                  slot
                ]?.length > 0 && (
                  <text
                    x={
                      x +
                      slotWidth / 2 +
                      5
                    }
                    y={
                      BASS_STAFF_TOP +
                      STAFF_HEIGHT +
                      35
                    }
                    textAnchor="middle"
                    fontSize="16"
                    fontWeight={600}
                    fill="#000"
                    pointerEvents="none"
                  >
                    {config.keyPrompts[
                      slot
                    ].join(" – ")}
                  </text>
                )}

                {slot < slots && (
                  <line
                    x1={x + slotWidth}
                    x2={x + slotWidth}
                    y1={SYSTEM_TOP}
                    y2={SYSTEM_BOTTOM - 20}
                    stroke="#000"
                    strokeWidth={1.2}
                    pointerEvents="none"
                  />
                )}
              </g>
            );
          })}

          <g pointerEvents="none">
            <line
              x1={
                LEFT_MARGIN +
                slots * slotWidth -
                6
              }
              x2={
                LEFT_MARGIN +
                slots * slotWidth -
                6
              }
              y1={SYSTEM_TOP}
              y2={SYSTEM_BOTTOM - 20}
              stroke="#000"
              strokeWidth={1.2}
            />

            <line
              x1={
                LEFT_MARGIN +
                slots * slotWidth -
                1.5
              }
              x2={
                LEFT_MARGIN +
                slots * slotWidth -
                1.5
              }
              y1={SYSTEM_TOP}
              y2={SYSTEM_BOTTOM - 20}
              stroke="#000"
              strokeWidth={3}
            />
          </g>
        </svg>
      </div>
    </QuestionLayout>
  );
}